import {
  Component,
  inject,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  selectEnvironmentType,
  selectEnvironmentLabel,
  selectEnvironmentInfo,
  selectBannerDismissed,
  environmentActions,
  type EnvironmentType,
} from '@core/state/environment';
import { InformationBoxComponent } from '@shared/information-box/information-box.component';
import { DatabaseDisplayNamePipe } from '@shared/pipes/database-display-name/database-display-name.pipe';
import { AppConfigService } from '@core/services/config/app-config.service';
import { getPreviewPullRequestMetadata } from './preview-pull-request-metadata';
import {
  PREVIEW_PULL_REQUEST_DESCRIPTION_DIALOG_TITLE,
  PreviewPullRequestDescriptionDialogComponent,
  PreviewPullRequestDescriptionDialogData,
} from './preview-pull-request-description-dialog.component';
import {
  SyrfDialogActionsDirective,
  SyrfDialogLayoutComponent,
} from '@shared/dialog/syrf-dialog-layout.component';
import {
  SyrfDialogConfig,
  SyrfDialogRef,
  SyrfDialogService,
  injectSyrfDialogRef,
} from '@shared/dialog/syrf-dialog.service';
import { MatExpButton } from '@camaradesuk/m3e-angular';

const STORAGE_KEY = 'syrf_env_warning_dismissed';

/** The id of the dialog's message, which describes it (`ariaDescribedBy`). */
export const ENVIRONMENT_WARNING_MESSAGE_ID = 'environment-warning-dialog-message';

/**
 * The heading openers pass as the dialog title for the current environment type
 * (`selectEnvironmentType`).
 */
export function environmentWarningDialogTitle(
  environmentType: EnvironmentType | null | undefined
): string {
  switch (environmentType) {
    case 'staging':
      return 'Staging Environment';
    case 'preview':
      return 'Preview Environment';
    case 'development':
      return 'Development Environment';
    case 'local':
      return 'Local Development';
    default:
      return 'Non-Production Environment';
  }
}

/**
 * Dialog shown when entering a non-production environment.
 * Warns users about the environment and allows them to dismiss for the session.
 * Both "I Understand" and "Show environment banner" close with `true`; Escape and
 * the backdrop close with `undefined`.
 */
@Component({
  selector: 'app-environment-warning-dialog',
  imports: [
    CommonModule,
    SyrfDialogLayoutComponent,
    SyrfDialogActionsDirective,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    FormsModule,
    InformationBoxComponent,
    DatabaseDisplayNamePipe,
    MatExpButton,
  ],
  template: `
    <syrf-dialog-layout>
      <div
        class="warning-dialog"
        [class]="'warning-dialog--' + environmentType()"
      >
        <p [id]="messageId" class="warning-dialog__message">
          <mat-icon class="warning-dialog__icon" aria-hidden="true"
            >warning</mat-icon
          >
          <span>{{ message() }}</span>
        </p>

        <div class="warning-dialog__info">
          <div class="warning-dialog__info-row">
            <span class="warning-dialog__label">Environment:</span>
            <span class="warning-dialog__value">{{ environmentLabel() }}</span>
          </div>
          @if (info()?.databaseName; as db) {
            <div class="warning-dialog__info-row">
              <span class="warning-dialog__label">Database:</span>
              <span
                class="warning-dialog__value"
                [class.warning-dialog__value--production]="
                  isProductionDatabase()
                "
              >
                {{
                  db
                    | databaseDisplayName
                      : $safeNavigationMigration(info()?.databaseType)
                }}
              </span>
            </div>
          }
          @if (info()?.databaseLocation; as loc) {
            <div class="warning-dialog__info-row">
              <span class="warning-dialog__label">Location:</span>
              <span class="warning-dialog__value">{{ loc }}</span>
            </div>
          }
          @if (info()?.prNumber; as pr) {
            <div class="warning-dialog__info-row">
              <span class="warning-dialog__label">Pull Request:</span>
              <span class="warning-dialog__value">#{{ pr }}</span>
            </div>
          }
        </div>

        @if (isPreview() && previewPullRequest().url; as prUrl) {
          <section class="warning-dialog__pr-details" aria-labelledby="preview-pr-details-title">
            <h3 id="preview-pr-details-title" class="warning-dialog__pr-heading">
              Pull request
            </h3>
            <p class="warning-dialog__pr-title">
              {{ previewPullRequest().title ?? fallbackPreviewTitle() }}
            </p>
            <div class="warning-dialog__pr-actions">
              @if (previewPullRequest().description; as description) {
                <button
                  mat-stroked-button
                  matExpButton
                  type="button"
                  class="warning-dialog__pr-description-button"
                  (click)="
                    openPullRequestDescription(
                      description,
                      previewPullRequest().title,
                      prUrl
                    )
                  "
                >
                  <mat-icon aria-hidden="true">description</mat-icon>
                  View description
                </button>
              } @else {
                <p class="warning-dialog__pr-empty">Description unavailable.</p>
              }
              <a
                class="warning-dialog__pr-link"
                [href]="prUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                <mat-icon fontSet="fa" fontIcon="fa-github" aria-hidden="true" />
                <span>
                  Open pull request on GitHub<mat-icon
                    class="warning-dialog__pr-link-icon"
                    aria-hidden="true"
                    >open_in_new</mat-icon
                  >
                  <span class="sr-only">
                    (opens in new tab)
                  </span>
                </span>
              </a>
            </div>
          </section>
        }

        @if (isNonProductionDatabase()) {
          <app-information-box warning>
            <strong>Your work will not be saved to the real SyRF</strong>
            <p class="warning-dialog__db-warning-text">
              This environment uses a separate test database. Any projects,
              annotations, or studies you create here will not appear when you
              use the main SyRF website and may be deleted at any time.
            </p>
          </app-information-box>
        }

        @if (isProductionDatabase()) {
          <div class="warning-dialog__production-warning">
            <mat-icon class="warning-dialog__production-icon">error</mat-icon>
            <div>
              <strong>Connected to Production Database</strong>
              <p class="warning-dialog__db-warning-text">
                Any changes you make will persist to the live system and affect
                real users. Please proceed with caution.
              </p>
            </div>
          </div>
        }

        <mat-checkbox
          [(ngModel)]="dontShowAgain"
          class="warning-dialog__checkbox"
        >
          Don't show this warning again for this session
        </mat-checkbox>

        @if (bannerDismissed()) {
          <button
            mat-button
            matExpButton
            class="warning-dialog__show-banner"
            (click)="showBanner()"
          >
            <mat-icon>visibility</mat-icon>
            Show environment banner
          </button>
        }
      </div>

      <ng-template syrfDialogActions>
        <button mat-flat-button matExpButton [color]="buttonColor()" (click)="acknowledge()">
          I Understand
        </button>
      </ng-template>
    </syrf-dialog-layout>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .warning-dialog {
        min-width: 400px;
        max-width: 500px;
      }

      /* The heading is the dialog title, so the environment colour that used to fill
         it marks the message instead. */
      .warning-dialog__message {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        margin: 0 0 16px 0;
        padding-left: 12px;
        border-left: 4px solid #607d8b;
        font-size: 14px;
        line-height: 1.5;

        .warning-dialog--staging & {
          border-left-color: #f57c00;
        }

        .warning-dialog--preview & {
          border-left-color: #7b1fa2;
        }

        .warning-dialog--development & {
          border-left-color: #1976d2;
        }

        .warning-dialog--local & {
          border-left-color: #455a64;
        }
      }

      .warning-dialog__icon {
        flex-shrink: 0;
        font-size: 20px;
        height: 20px;
        width: 20px;
      }

      .warning-dialog__info {
        background: #f5f5f5;
        border-radius: 8px;
        padding: 14px 16px;
        margin-bottom: 20px;
      }

      .warning-dialog__info-row {
        display: flex;
        gap: 16px;
        justify-content: space-between;
        padding: 5px 0;
      }

      .warning-dialog__label {
        font-weight: 500;
        color: #666;
      }

      .warning-dialog__value {
        font-family: monospace;
        color: #333;
      }

      .warning-dialog__value--production {
        color: #d32f2f;
        font-weight: 600;
        background: #ffebee;
        padding: 2px 8px;
        border-radius: 4px;
      }

      .warning-dialog__checkbox {
        display: block;
        margin-top: 4px;
      }

      .warning-dialog__db-warning-text {
        margin: 8px 0 0 0;
        font-size: 13px;
        line-height: 1.4;
      }

      .warning-dialog__production-warning {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        background: #ffebee;
        border: 1px solid #ffcdd2;
        border-left: 4px solid #d32f2f;
        border-radius: 4px;
        padding: 12px 16px;
        margin-bottom: 16px;
        color: #c62828;

        strong {
          color: #b71c1c;
        }
      }

      .warning-dialog__production-icon {
        color: #d32f2f;
        font-size: 24px;
        height: 24px;
        width: 24px;
        flex-shrink: 0;
      }

      .warning-dialog__show-banner {
        display: flex;
        align-items: center;
        gap: 4px;
        margin-top: 16px;
        padding: 0;
        color: #1976d2;
        font-size: 13px;

        mat-icon {
          font-size: 18px;
          height: 18px;
          width: 18px;
        }
      }

      .warning-dialog__pr-details {
        border-top: 1px solid #e0e0e0;
        border-bottom: 1px solid #e0e0e0;
        margin: 0 0 20px;
        padding: 16px 0;
      }

      .warning-dialog__pr-heading {
        color: #666;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        margin: 0 0 6px;
        text-transform: uppercase;
      }

      .warning-dialog__pr-title {
        font-weight: 600;
        line-height: 1.4;
        margin: 0;
        overflow-wrap: anywhere;
      }

      .warning-dialog__pr-actions {
        align-items: center;
        column-gap: 16px;
        display: flex;
        flex-wrap: wrap;
        margin-top: 12px;
        row-gap: 8px;
      }

      .warning-dialog__pr-empty {
        color: #666;
        font-size: 13px;
        margin: 0;
      }

      .warning-dialog__pr-link {
        align-items: flex-start;
        display: inline-flex;
        gap: 6px;
        text-decoration: none;

        &:hover,
        &:focus-visible {
          text-decoration: underline;
        }

        > mat-icon {
          flex-shrink: 0;
          font-size: 18px;
          height: 18px;
          margin-top: 2px;
          width: 18px;
        }
      }

      /* Inline so it follows the last word when the label wraps. */
      .warning-dialog__pr-link-icon {
        font-size: 16px;
        height: 16px;
        margin-left: 4px;
        opacity: 0.7;
        vertical-align: text-bottom;
        width: 16px;
      }


      @media (max-width: 600px) {
        .warning-dialog {
          min-width: 0;
          width: min(100%, 500px);
        }

        .warning-dialog__info-row {
          align-items: flex-start;
          flex-direction: column;
          gap: 2px;
        }
      }
    `,
  ],
})
export class EnvironmentWarningDialogComponent {
  protected readonly messageId = ENVIRONMENT_WARNING_MESSAGE_ID;

  private readonly store = inject(Store);
  private readonly appConfig = inject(AppConfigService);
  private readonly dialog = inject(SyrfDialogService);
  private readonly dialogRef = injectSyrfDialogRef<
    EnvironmentWarningDialogComponent,
    true
  >();

  // Store selectors as signals
  protected readonly environmentType = this.store.selectSignal(
    selectEnvironmentType
  );
  protected readonly environmentLabel = this.store.selectSignal(
    selectEnvironmentLabel
  );
  protected readonly info = this.store.selectSignal(selectEnvironmentInfo);
  protected readonly bannerDismissed = this.store.selectSignal(
    selectBannerDismissed
  );

  protected readonly isPreview = computed(() => this.environmentType() === 'preview');
  protected readonly previewPullRequest = computed(() =>
    getPreviewPullRequestMetadata(
      this.info()?.prNumber,
      this.appConfig.config.previewPullRequestTitleBase64,
      this.appConfig.config.previewPullRequestDescriptionBase64
    )
  );
  protected readonly fallbackPreviewTitle = computed(() => {
    const prNumber = this.info()?.prNumber;
    return prNumber ? `Pull Request #${prNumber}` : 'Pull Request';
  });

  protected dontShowAgain = false;

  /**
   * Returns true if the database is not the production database.
   * Uses server-derived databaseType for unambiguous identification.
   */
  protected readonly isNonProductionDatabase = computed(() => {
    const dbType = this.info()?.databaseType;
    if (!dbType) return false;
    return dbType !== 'production';
  });

  /**
   * Returns true if connected to the production database.
   * Uses server-derived databaseType for unambiguous identification.
   */
  protected readonly isProductionDatabase = computed(() => {
    return this.info()?.databaseType === 'production';
  });

  protected readonly message = computed(() => {
    const envType = this.environmentType();
    const info = this.info();

    if (envType === 'staging') {
      return 'You are using the staging environment. Changes made here may affect shared testing data.';
    }

    if (envType === 'preview') {
      return `You are viewing a preview build for Pull Request #${info?.prNumber}. This environment is for testing changes before they are merged.`;
    }

    if (envType === 'local') {
      return 'You are running a local development instance. This environment is isolated to your machine.';
    }

    return 'You are not using the production environment. Data and features may differ from production.';
  });

  protected readonly buttonColor = computed(
    (): 'primary' | 'accent' | 'warn' => {
      const envType = this.environmentType();
      switch (envType) {
        case 'staging':
          return 'warn';
        case 'preview':
          return 'accent';
        default:
          return 'primary';
      }
    }
  );

  /**
   * Open the pull-request description in its own dialog, rendered as Markdown.
   *
   * Focus returns to the invoking button when that dialog closes, and this
   * dialog stays open behind it.
   */
  protected openPullRequestDescription(
    description: string,
    title: string | null,
    url: string
  ): void {
    this.dialog.open<
      PreviewPullRequestDescriptionDialogComponent,
      PreviewPullRequestDescriptionDialogData
    >(PreviewPullRequestDescriptionDialogComponent, {
      mode: 'adaptive',
      title: PREVIEW_PULL_REQUEST_DESCRIPTION_DIALOG_TITLE,
      data: {
        title: title ?? this.fallbackPreviewTitle(),
        description,
        url,
      },
      width: 'min(92vw, 46rem)',
      maxHeight: '85vh',
      // Announce the dialog itself rather than opening with the GitHub link
      // already ringed; Tab still reaches it first.
      autoFocus: 'dialog',
    });
  }

  protected acknowledge(): void {
    if (this.dontShowAgain) {
      sessionStorage.setItem(STORAGE_KEY, 'true');
    }
    this.dialogRef.close(true);
  }

  /**
   * Restore the environment banner and close the dialog.
   */
  protected showBanner(): void {
    this.store.dispatch(environmentActions.showBanner());
    this.dialogRef.close(true);
  }

  /**
   * Check if the warning should be shown for this session.
   */
  static shouldShowWarning(): boolean {
    return sessionStorage.getItem(STORAGE_KEY) !== 'true';
  }

  /**
   * Opens the warning for an environment type: the one place that holds its title, presentation and the message
   * that describes it. Openers pass only what differs for them.
   */
  static open(
    dialog: SyrfDialogService,
    environmentType: EnvironmentType | null | undefined,
    overrides: Pick<SyrfDialogConfig, 'width' | 'autoFocus' | 'disableClose'> = {}
  ): SyrfDialogRef<EnvironmentWarningDialogComponent, boolean> {
    return dialog.open<EnvironmentWarningDialogComponent, unknown, boolean>(
      EnvironmentWarningDialogComponent,
      {
        mode: 'adaptive',
        title: environmentWarningDialogTitle(environmentType),
        width: '450px',
        autoFocus: true,
        ariaDescribedBy: ENVIRONMENT_WARNING_MESSAGE_ID,
        ...overrides,
      }
    );
  }
}
